import { extractEdgeVertices } from '../../common';
import type { ChamferKernelInstance } from '../types/ChamferShapeBuildInput';
import {
  chooseEdgeFarPoint,
  EDGE_DISTANCE_EPS,
  normalizeVector,
  vectorLength,
  vectorSubtract,
  type Vector3Tuple,
} from './chamferVector';

/**
 * 解析顶点倒角三向距离。
 *
 * @param definition 倒角定义。
 * @returns 三向距离。
 */
export function resolveVertexChamferDistances(definition: ChamferFeatureDefinition): [number, number, number] {
  if (definition.equalDistance) {
    const distance = definition.distance ?? definition.vertexDistances?.[0] ?? null;
    if (typeof distance !== 'number' || !Number.isFinite(distance) || distance <= 0) {
      throw new Error('顶点倒角缺少有效等距值');
    }
    return [distance, distance, distance];
  }

  if (!Array.isArray(definition.vertexDistances) || definition.vertexDistances.length !== 3) {
    throw new Error('顶点倒角缺少三向距离参数');
  }
  const nextDistances = definition.vertexDistances.map((distance) => Number(distance));
  if (nextDistances.some((distance) => !Number.isFinite(distance) || distance <= 0)) {
    throw new Error('顶点倒角的三向距离必须全部大于 0');
  }
  return nextDistances as [number, number, number];
}

/**
 * 构造顶点倒角切角点。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param incidentEdges 相邻边集合。
 * @param vertexPoint 顶点坐标。
 * @param distances 三向距离。
 * @returns 切角点集合。
 */
export function buildVertexChamferCutPoints(
  puppy: ChamferKernelInstance,
  incidentEdges: unknown[],
  vertexPoint: Vector3Tuple,
  distances: [number, number, number],
): Vector3Tuple[] {
  return incidentEdges.map((edgeShape, index) => {
    const edgeVertices = extractEdgeVertices(puppy, edgeShape);
    const farPoint = chooseEdgeFarPoint(edgeVertices, vertexPoint);
    const edgeVector = vectorSubtract(farPoint, vertexPoint);
    const edgeLength = vectorLength(edgeVector);
    const offsetDistance = distances[index]!;
    if (edgeLength + EDGE_DISTANCE_EPS < offsetDistance) {
      throw new Error('顶点倒角距离超过了相邻边长度');
    }
    const direction = normalizeVector(edgeVector);
    return [
      vertexPoint[0] + direction[0] * offsetDistance,
      vertexPoint[1] + direction[1] * offsetDistance,
      vertexPoint[2] + direction[2] * offsetDistance,
    ];
  });
}

